import { useCallback, useEffect, useState } from "react";
import {
  API_URL, ORDERS_API, CLIENTS_API, CURRENCIES_API, ACCOUNTS_API,
  PROFIT_ALLOCATIONS_URL, SUPPLIER_PREPAYMENTS_URL,
} from "./constants";
import { getOrderCode, getClientId, requestJson, fetchAllPages } from "./helpers";

export function usePaymentData(id) {
  const [payment, setPayment] = useState(null);
  const [orders, setOrders] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [profitAllocations, setProfitAllocations] = useState([]);
  const [supplierPrepayments, setSupplierPrepayments] = useState([]);

  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [error, setError] = useState("");

  const loadPayment = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await requestJson(`${API_URL}${id}/`);
      setPayment(data);
    } catch (err) {
      setError(err.message || "Не удалось загрузить платёж.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError("");
    try {
      const [orderData, clientsData] = await Promise.all([
        fetchAllPages(ORDERS_API),
        fetchAllPages(CLIENTS_API).catch(() => []),
      ]);
      const clientsById = new Map(clientsData.map((c) => [String(c.id), c]));
      const formatted = orderData
        .filter((o) => o.id != null && getOrderCode(o))
        .map((order) => {
          const nestedClient =
            order.client && typeof order.client === "object" ? order.client : null;
          const clientId = nestedClient?.id ?? order.client_id ?? order.client;
          const client = nestedClient || clientsById.get(String(clientId));
          const clientName =
            order.client_name ||
            nestedClient?.name ||
            nestedClient?.client_name ||
            client?.name ||
            client?.client_name ||
            client?.full_name ||
            "";
          return { ...order, _clientName: clientName };
        });
      setOrders(formatted);
    } catch (err) {
      setOrdersError(err.message || "Не удалось загрузить заказы.");
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const loadCurrencies = useCallback(async () => {
    try {
      const data = await requestJson(CURRENCIES_API);
      setCurrencies(Array.isArray(data) ? data : data?.results || []);
    } catch {
      setCurrencies([]);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      setAccounts(await fetchAllPages(ACCOUNTS_API));
    } catch (err) {
      console.error("accounts:", err);
    }
  }, []);

  const loadDistribution = useCallback(async () => {
    try {
      const [profit, supplier] = await Promise.all([
        fetchAllPages(PROFIT_ALLOCATIONS_URL),
        fetchAllPages(SUPPLIER_PREPAYMENTS_URL),
      ]);
      setProfitAllocations(profit);
      setSupplierPrepayments(supplier);
    } catch (err) {
      console.error("distribution:", err);
    }
  }, []);

  useEffect(() => {
    loadPayment();
    loadOrders();
    loadCurrencies();
    loadAccounts();
    loadDistribution();
  }, [loadPayment, loadOrders, loadCurrencies, loadAccounts, loadDistribution]);

  return {
    payment, setPayment,
    orders, ordersLoading, ordersError,
    currencies, accounts,
    profitAllocations, supplierPrepayments,
    loading, error,
    reloadPayment: loadPayment,
    reloadDistribution: loadDistribution,
  };
}